import React, { useState, useEffect, useContext } from 'react';
import { DataTable, Notifications } from '../../../shared';
import { searchApi as api } from '../../../api/search';
import { UserContext } from '../../../contexts/UserContext';
import {
    TextField,
    Button,
    Typography,
    Box,
    Grid,
    Paper,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Tooltip,
    IconButton,
} from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';

const ResultItem = ({ item }) => (
    <React.Fragment>
        {item.brand ? (
            <React.Fragment>
                <strong>{item.brand}</strong> {item.name || 'Unnamed item'}
            </React.Fragment>
        ) : (
            item.name || 'Unnamed item'
        )}
        {item.sponsored && (
            <Tooltip title="Sponsored Result">
                <MonetizationOnIcon fontSize="small" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
            </Tooltip>
        )}
    </React.Fragment>
);

const SearchCompare = ({}) => {
    let user = useContext(UserContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [notification, setNotification] = useState({ text: '', level: '' });
    const [isBustingCache, setIsBustingCache] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isForcingCache, setIsForcingCache] = useState(false);
    const [packGroupParams, setPackGroupParams] = useState(null);

    const [similarityThreshold, setSimilarityThreshold] = useState('');
    const [l2Boost, setL2Boost] = useState('');
    const [l1Boost, setL1Boost] = useState('');
    const [itemNameBoost, setItemNameBoost] = useState('');
    const [brandBoost, setBrandBoost] = useState('');
    const [numResults, setNumResults] = useState('');

    const [llmResults, setLlmResults] = useState(null);
    const [isLlmLoading, setIsLlmLoading] = useState(false);
    const [llmError, setLlmError] = useState(null);

    const [vectorResults, setVectorResults] = useState(null);
    const [isVectorLoading, setIsVectorLoading] = useState(false);
    const [vectorError, setVectorError] = useState(null);

    useEffect(() => {
        if (user && typeof user.getFacilityID === 'function') {
            const facilityID = user.getFacilityID();
            api.getSearchPackGroupParameters(facilityID)
                .then((response) => {
                    if (response && response.data && response.data.data) {
                        setPackGroupParams(response.data.data);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching pack group parameters:', error);
                    setNotification({ text: 'Failed to fetch pack group parameters.', level: 'error' });
                });
        }
    }, [user]);

    const handleForceCacheConfirm = async () => {
        setIsConfirmModalOpen(false);
        setIsForcingCache(true);
        setNotification({ text: 'Forcing cache update...', level: 'info' });

        try {
            const facilityID = user.getFacilityID();
            const trimmedSearchTerm = searchTerm.trim();
            await api.checkLLMSearchResults(trimmedSearchTerm, facilityID, { forceCacheIfNotFound: true });
            setNotification({ text: 'Cache updated. Refreshing all results...', level: 'success' });
            await handleSearchSubmit({ preventDefault: () => {} });
        } catch (error) {
            console.error('Error forcing cache:', error);
            setNotification({ text: 'Failed to force cache.', level: 'error' });
        } finally {
            setIsForcingCache(false);
        }
    };

    const handleBustCache = async () => {
        setIsBustingCache(true);
        try {
            await api.bustLLMCache(searchTerm.trim());
            setNotification({ text: 'Cache busted successfully. Re-searching...', level: 'success' });
            handleSearchSubmit({ preventDefault: () => {} });
        } catch (error) {
            console.error('Failed to bust cache:', error);
            setNotification({ text: 'Failed to bust cache.', level: 'error' });
        } finally {
            setIsBustingCache(false);
        }
    };

    const handleSearchSubmit = async (event) => {
        event.preventDefault();
        if (!searchTerm.trim() || !user || typeof user.getFacilityID !== 'function') {
            const errorMsg = 'Search term is empty or facility ID is unavailable.';
            setLlmError(errorMsg);
            setVectorError(errorMsg);
            return;
        }

        const facilityID = user.getFacilityID();
        const trimmedSearchTerm = searchTerm.trim();

        setIsLlmLoading(true);
        setLlmError(null);
        setLlmResults(null);
        api.checkLLMSearchResults(trimmedSearchTerm, facilityID)
            .then((llmResponse) => {
                if (llmResponse && llmResponse.data) {
                    setLlmResults(llmResponse.data.rows || llmResponse.data);
                } else {
                    setLlmResults([]);
                }
            })
            .catch((error) => {
                console.error('Error fetching LLM search results:', error);
                setLlmError('Failed to fetch LLM search results.');
                setLlmResults([]);
            })
            .finally(() => {
                setIsLlmLoading(false);
            });

        setIsVectorLoading(true);
        setVectorError(null);
        setVectorResults(null);

        const vectorSearchParams = {};
        if (similarityThreshold !== '') vectorSearchParams.similarityThreshold = parseFloat(similarityThreshold);
        if (l2Boost !== '') vectorSearchParams.l2Boost = parseFloat(l2Boost);
        if (l1Boost !== '') vectorSearchParams.l1Boost = parseFloat(l1Boost);
        if (itemNameBoost !== '') vectorSearchParams.nameBoost = parseFloat(itemNameBoost);
        if (brandBoost !== '') vectorSearchParams.brandBoost = parseFloat(brandBoost);
        if (numResults !== '') vectorSearchParams.numResults = parseInt(numResults, 10);

        api.vectorSearch(trimmedSearchTerm, facilityID, vectorSearchParams)
            .then((vectorResponse) => {
                if (vectorResponse && vectorResponse.data) {
                    setVectorResults(vectorResponse.data.rows || vectorResponse.data);
                } else {
                    setVectorResults([]);
                }
            })
            .catch((error) => {
                console.error('Error fetching Vector search results:', error);
                setVectorError('Failed to fetch Vector search results.');
                setVectorResults([]);
            })
            .finally(() => {
                setIsVectorLoading(false);
            });
    };

    const ConfirmationModal = () => (
        <Dialog open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
            <DialogTitle>Confirm Cache Update</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    This will perform a new search and update the results that users see in the marketplace, replacing
                    the current Vector Search results. Are you sure you want to proceed?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setIsConfirmModalOpen(false)} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleForceCacheConfirm} color="primary" autoFocus>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <>
            <Notifications options={notification} />
            <ConfirmationModal />
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Compare Search Results
                </Typography>
                {packGroupParams && (
                    <Typography variant="body1" gutterBottom>
                        <strong>Pack Group ID:</strong> {packGroupParams.packGroupID}, <strong>Week:</strong>{' '}
                        {packGroupParams.week}, <strong>Year:</strong> {packGroupParams.year}
                    </Typography>
                )}
                <Typography variant="body1" gutterBottom>
                    Compare results for a search term for the live pack group.
                </Typography>

                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
                >
                    <TextField
                        label="Search Term"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mr: 1, flexGrow: 1 }}
                    />
                    <Button type="submit" variant="contained" color="primary" disabled={!searchTerm.trim()}>
                        Search
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom align="center">
                                LLM Search
                            </Typography>
                            <Box sx={{ height: 112 }} />
                            {isLlmLoading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexGrow: 1,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            ) : llmError ? (
                                <Typography color="error" align="center" sx={{ mt: 2 }}>
                                    {llmError}
                                </Typography>
                            ) : llmResults && llmResults.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <List dense>
                                        {llmResults.map((item, index) => (
                                            <ListItem key={item.id || index}>
                                                <ListItemText primary={<ResultItem item={item} />} />
                                            </ListItem>
                                        ))}
                                    </List>
                                    <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={handleBustCache}
                                            disabled={isBustingCache}
                                        >
                                            {isBustingCache ? <CircularProgress size={24} /> : 'Bust Cache'}
                                        </Button>
                                    </Box>
                                </Box>
                            ) : llmResults && llmResults.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
                                        No results found.
                                    </Typography>
                                    <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => setIsConfirmModalOpen(true)}
                                            disabled={isForcingCache || isLlmLoading}
                                        >
                                            {isForcingCache ? <CircularProgress size={24} /> : 'Add Results to Cache'}
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
                                    (Enter a search term and click Search)
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom align="center">
                                Vector Search
                            </Typography>

                            <Box sx={{ p: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Similarity Threshold"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            value={similarityThreshold}
                                            onChange={(e) => setSimilarityThreshold(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    <Tooltip title="Similarity Threshold is a value from 0 to 1 that determines how similar the search term must be to the vector to be included in the results. 1 is the most similar, 0 is the least similar. Default: 0.1">
                                                        <IconButton size="small">
                                                            <HelpOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ),
                                                inputProps: { step: '0.01' },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="L2 Boost"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            value={l2Boost}
                                            onChange={(e) => setL2Boost(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    <Tooltip title="Amount to boost the score when the search term is similar to the L2 category. Default: 0.6">
                                                        <IconButton size="small">
                                                            <HelpOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ),
                                                inputProps: { step: '0.01' },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="L1 Boost"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            value={l1Boost}
                                            onChange={(e) => setL1Boost(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    <Tooltip title="Amount to boost the score when the search term is similar to the L1 category. Default: 0.45">
                                                        <IconButton size="small">
                                                            <HelpOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ),
                                                inputProps: { step: '0.01' },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Name Boost"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            value={itemNameBoost}
                                            onChange={(e) => setItemNameBoost(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    <Tooltip title="Amount to boost the score when the search term is similar to the name. Default: 0.3">
                                                        <IconButton size="small">
                                                            <HelpOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ),
                                                inputProps: { step: '0.01' },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Brand Boost"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            value={brandBoost}
                                            onChange={(e) => setBrandBoost(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    <Tooltip title="Amount to boost the score when the search term is similar to the brand. Default: 0.4">
                                                        <IconButton size="small">
                                                            <HelpOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ),
                                                inputProps: { step: '0.01' },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Number of Results"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            value={numResults}
                                            onChange={(e) => setNumResults(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    <Tooltip title="The maximum number of results to return. Default: 15">
                                                        <IconButton size="small">
                                                            <HelpOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ),
                                                inputProps: { step: '1' },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {isVectorLoading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexGrow: 1,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            ) : vectorError ? (
                                <Typography color="error" align="center" sx={{ mt: 2 }}>
                                    {vectorError}
                                </Typography>
                            ) : vectorResults && vectorResults.length > 0 ? (
                                <List dense>
                                    {vectorResults.map((item, index) => (
                                        <ListItem key={item.id || index}>
                                            <ListItemText primary={<ResultItem item={item} />} />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : vectorResults && vectorResults.length === 0 ? (
                                <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
                                    No results found.
                                </Typography>
                            ) : (
                                <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
                                    (Enter a search term and click Search)
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export { SearchCompare };
